import { DateTime } from 'luxon';
import classnames from 'classnames';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { BnDialog } from '~/common';
import { Box, CircularProgress, DialogContent, makeStyles, Typography } from '~/lib/bn-material-ui';
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import { SelectionContextProvider, setSelection, useSelectionContext } from '~/common/bn-selection-context';
import { OrderFeedType } from '~/orders/components/order-feed-type';
import {
  entityStore,
  sirenActions,
  useMeasurement,
  useSirenEntity,
} from '~/utility/BinSentry-ui-utility';
import BnSelectionCheckbox from '~/common/bn-selection-checkbox';
import parseSiren from 'siren-parser';
import makeSuperResponsiveTableStyles from '~/common/react-super-responsive-table-styles';
import { BarnLink } from '~/bins/barn-link';
import { BinLink } from '~/bins/bin-link';
import { SiteLink } from '~/bins/site-link';
import OrderQuantity from '~/orders/components/order-quantity';
import { useFileDownloader } from '~/common/download-file';

const useStyles = makeStyles(theme => {
  const pagePadding = theme.spacing(4);
  const previouslyExportedColor = theme.palette.blue[40];
  const errorColor = theme.palette.red[20];
  return {
    container: {
      '& table': {
        borderCollapse: 'collapse',
      },
      '& thead > tr > th': {
        textAlign: 'left',
      },
      '& tbody td': {
        paddingTop: '0.2em',
        paddingBottom: '0.2em',
      },
      [theme.breakpoints.up('md')]: {
        '& table': {
          tableLayout: 'fixed',
        },
        '& td': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '& tbody > tr': {
          height: '54px',
        },
        '& table > thead > tr > th.order-table-selection': {
          width: '3rem',
          '& .MuiCheckbox-root': {
            padding: 0,
          },
        },
        '& table > thead > tr > th.order-table-date': {
          width: '10em',
        },
        '& table > thead > tr > th.order-table-amount': {
          width: '9em',
        },
      },
      [theme.breakpoints.down('md')]: {
        '& table tr': {
          border: '0 !important',
          padding: `0 ${pagePadding} !important`,
          minHeight: '20px',
        },
        '& table tbody': {
          marginBottom: '5em',
        },
        '& table td:not(:last-child)': {
          paddingRight: 0,
          marginTop: '0.5em',
        },
        '& table td.pivoted.order-table-selection .tdBefore': {
          lineHeight: '1.5rem',
        },
        '& table td.pivoted.order-table-selection > span': {
          display: 'inline-block',
          width: '100%',
          textAlign: 'right',
          paddingRight: '0.5em',
        },
        '& table td .tdBefore': {
          textTransform: 'uppercase',
          color: theme.palette.gray[60],
          transform: 'translateX(-1rem)', // negate "left: 1rem"
        },
      },
    },
    feedTypeColumn: {
      display: 'inline-flex',
      alignItems: 'baseline',
    },
    medicatedIcon: {
      color: theme.palette.gray[60],
      marginLeft: '.5rem',
      fontSize: '1.2rem',
    },
    previouslyExportedWrap: {
      '&&': {
        border: `2px solid ${previouslyExportedColor} !important`,
      },
    },
    errorWrap: {
      '&&': {
        // more important than previously exported
        border: `2px solid ${errorColor} !important`,
      },
    },
    messageRow: {
      '&&': {
        height: 'auto',
      },
      '&& td': {
        paddingBottom: '1rem',
      },
      '& p': {
        textAlign: 'right',
      },
    },
    errorMessage: {
      color: errorColor,
    },
    previouslyExportedMessage: {
      color: previouslyExportedColor,
    },
    dialog: {
      '& .MuiDialogTitle-root': {
        paddingBottom: 0,
      },
    },
    legend: {
      '&:first-of-type': {
        marginTop: 0,
      },
    },
    hiddenMdUp: {
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },
  };
});

const useResponsiveTableStyles = makeSuperResponsiveTableStyles('md');

export default function EaiExportDialog({ ...forwardedArgs }) {
  return <SelectionContextProvider>
    <ExportDialog {...forwardedArgs}/>
  </SelectionContextProvider>;
}

function ExportDialog({ open, handleClose, eaiIntegrationHref }) {
  const styles = useStyles();
  const [eaiValidationEntity, setEaiValidationEntity] = useState(null);
  const downloadFile = useFileDownloader();
  const [selectionState] = useSelectionContext();

  useEffect(
    function doLoadOnOpen() {
      let cancelled = false;
      async function doLoad() {
        const integrationEntity = parseSiren((await entityStore.fetchWait(eaiIntegrationHref))?.entity);
        const action = integrationEntity?.getActionByName('validate-orders');
        const { entity: validationEntity } = action ? await sirenActions.submitAction(action) : {};
        if (!cancelled) {
          setEaiValidationEntity(validationEntity);
        }
      }

      setEaiValidationEntity(undefined);
      if (open) {
        doLoad();
      }
      return () => cancelled = true;
    },
    [open, eaiIntegrationHref],
  );

  const doDownload = useCallback(
    async function doDownload(orderIds) {
      if (!eaiValidationEntity || !orderIds) {
        // button should be disabled in these cases anyway
        return;
      }

      const exportAction = eaiValidationEntity.getActionByName('export-orders');
      if (!exportAction) {
        return;
      }

      const exportEntity = parseSiren((await sirenActions.submitAction(exportAction, { orderIds }, { appendArrays: true }))?.entity);
      const xml = exportEntity.properties.xml;
      const xmlUrl = `data:text/plain;charset=UTF-8,${encodeURIComponent(xml)}`;
      await downloadFile({ url: xmlUrl, fileName: `feedofficepro-export-${DateTime.now().toISODate()}.xml` });
      return true;
    },
    [downloadFile, eaiValidationEntity],
  );

  const handleSubmit = useCallback(() => doDownload(Object.keys(selectionState.selectedIds)), [doDownload, selectionState]);

  return <BnDialog
    className={styles.dialog}
    maxWidth='md'
    fullWidth={true}
    open={open}
    onClose={handleClose}
    closeOnSuccess={true}
    onSubmit={handleSubmit}
    submitDisabled={!selectionState.hasSelection}
    submitLabel='Export for EAI'
    title='Export for EAI'>
    <DialogContent className={styles.container}>
      <ExportDialogContent eaiValidationEntity={eaiValidationEntity}/>
    </DialogContent>
  </BnDialog>;
}
function ExportDialogContent({ eaiValidationEntity }) {
  const tableStyles = useResponsiveTableStyles();
  const styles = useStyles();
  const [, dispatchSelection] = useSelectionContext();

  useEffect(function setInitialSelection() {
    if (eaiValidationEntity) {
      const orderValidationResults = eaiValidationEntity.getSubEntitiesByRel('item');
      const selectedIds = orderValidationResults
        .filter(result => result.properties.valid && !result.properties.lastExportedAt)
        .map(result => result.getLinkByRel('https://api.binsentry.com/rel/order').href);
      dispatchSelection(setSelection(selectedIds));
    }
  }, [eaiValidationEntity, dispatchSelection]);

  if (!eaiValidationEntity) {
    return <CircularProgress/>;
  }

  const orderValidationResults = eaiValidationEntity.getSubEntitiesByRel('item');
  const hasOrdersWithErrors = orderValidationResults.some(result => !result.properties.valid);
  const hasPreviouslyExportedOrders = orderValidationResults.some(result => !!result.properties.lastExportedAt);

  return <>
    {hasPreviouslyExportedOrders
      ? <p className={styles.legend}>Orders highlighted in <span className={styles.previouslyExportedMessage}>blue</span> have been previously
        exported. These orders will not be imported in EAI unless you first delete the existing records.</p>
      : undefined
    }
    {hasOrdersWithErrors
      ? <p className={styles.legend}>Orders highlighted in <span className={styles.errorMessage}>red</span> cannot be exported due to errors.</p>
      : undefined
    }

    <Table className={tableStyles.responsiveTable}>
      <Thead>
        <Tr>
          <Th className='order-table-selection'>
            <Box className={styles.hiddenMdUp}>Selected</Box>
          </Th>
          <Th className='order-table-date'>Date</Th>
          <Th className='order-table-site'>Site</Th>
          <Th className='order-table-barn'>Barn</Th>
          <Th className='order-table-bin'>Bin</Th>
          <Th className='order-table-feed'>Feed</Th>
          <Th className='order-table-amount'>Amount</Th>
        </Tr>
      </Thead>
      {orderValidationResults.map(orderValidationResult =>
        <ExportStatusRow
          key={orderValidationResult.getLinkByRel('https://api.binsentry.com/rel/order').href}
          orderValidationResult={orderValidationResult}
        />)
      }
    </Table>
  </>;
}

function ExportStatusRow({ orderValidationResult }) {
  const styles = useStyles();

  const orderId = orderValidationResult.getLinkByRel('https://api.binsentry.com/rel/order').href;
  const { entity: order } = useSirenEntity({ href: orderId });
  const binHref = order?.getSubEntityByRel('https://api.binsentry.com/rel/bin')?.href;
  const feedHref = order?.getSubEntityByRel('https://api.binsentry.com/rel/feed')?.href;
  const { entity: feedEntity } = useSirenEntity({ href: feedHref });
  const orderAmountPreference = useMeasurement({ name: 'orderAmount' });

  const hasError = !orderValidationResult.properties.valid;
  const previouslyExported = !!orderValidationResult.properties.lastExportedAt;

  return <Tbody>
    <Tr className={classnames(
      hasError ? styles.errorWrap : undefined,
      previouslyExported ? styles.previouslyExportedWrap : undefined,
    )}>
      <Td className='order-table-selection'><span><BnSelectionCheckbox id={orderId} disabled={hasError}/></span></Td>
      <Td
        className='order-table-date'>{order?.properties.deliveryDate ? DateTime.fromISO(order.properties.deliveryDate).toLocaleString(DateTime.DATE_FULL) : ''}</Td>
      <Td className='order-table-site'><SiteLink binHref={binHref}/></Td>
      <Td className='order-table-barn'><BarnLink binHref={binHref}/></Td>
      <Td className='order-table-bin'><BinLink binHref={binHref}/></Td>
      <Td className='order-table-feed'><div className={styles.feedTypeColumn}>
        <OrderFeedType
          orderEntity={order}
          iconClassName={styles.medicatedIcon}
          unknownFeedTypeText='No feed type selected'
        />
      </div></Td>
      <Td className='order-table-amount'> <OrderQuantity
        feedEntity={feedEntity}
        orderEntity={order}
        orderAmountPreference={orderAmountPreference}
      /></Td>
    </Tr>
    {hasError ?
      <Tr className={classnames(
        styles.messageRow,
        hasError ? styles.errorMessage : undefined,
        previouslyExported ? styles.previouslyExportedMessage : undefined,
      )}>
        <Td colSpan='7'>
          {orderValidationResult.properties.errors?.map((error, i) => <Typography variant='caption' component='p'
            key={`error-${i}`}>Error: {error}</Typography>) || []}
        </Td>
      </Tr>
      : undefined}
  </Tbody>;
}
